import { useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { NotificationPanel } from "./NotificationPanel";
import { useNotifications } from "@/hooks/useNotifications";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearAllRead,
    clearOne,
    isClearingRead,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNotifications();

  // Auto-mark all as read 2s after opening
  useEffect(() => {
    if (!open || unreadCount === 0) return;
    const t = setTimeout(() => markAllAsRead(), 2000);
    return () => clearTimeout(t);
  }, [open, unreadCount, markAllAsRead]);

  const panelProps = {
    notifications,
    onMarkAsRead: markAsRead,
    onClearAllRead: clearAllRead,
    onClearOne: clearOne,
    isClearingRead,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore: fetchNextPage,
    onClose: () => setOpen(false),
  };

  const trigger = (
    <Button
      variant="ghost"
      size="icon"
      className="relative h-9 w-9"
      onClick={isMobile ? () => setOpen(true) : undefined}
    >
      <Bell className="h-5 w-5" />
      {unreadCount > 0 && (
        <span
          className={cn(
            "absolute top-0 right-0 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold",
            unreadCount > 9 ? "h-5 w-5" : "h-4 w-4"
          )}
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </Button>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            side="bottom"
            className="h-[85vh] supports-[height:1dvh]:h-[85dvh] p-0 rounded-t-2xl flex flex-col [&>button]:hidden"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Notifications</SheetTitle>
            </SheetHeader>
            <NotificationPanel {...panelProps} />
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 flex flex-col h-[480px] max-h-[var(--radix-dropdown-menu-content-available-height)]">
        <NotificationPanel {...panelProps} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
